'use client'

import { useId } from "react"
import { usePathname } from "next/navigation"
import { DateTimePicker } from "@/components/elements/DateTimePicker"
import { formatImageURL, formatPrice } from "@/utils/util"
import { navigateUrl } from "@/utils/client-request"
import { GIFT_CARD_MIN_AMOUNT, GIFT_CARD_MAX_AMOUNT, GIFT_CARD_PRESETS } from "@/utils/giftCard"
import { useGiftCardForm } from "@/lib/commerce/hooks"
import { TextField } from "../elements/Field"
import { panelFocusRing } from "../elements/SidePanel"

const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `h-12 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${panelFocusRing}`
const STEPS = ['Дизайн', 'Үнийн дүн', 'Хүлээн авагч']

function GiftCardPreview({ template, amount, recipient, message, vendorData }: { template: any, amount: string, recipient: string, message: string, vendorData: any }) {
  const value = amount && parseInt(amount, 10) > 0 ? parseInt(amount, 10) : null
  const thumbnail = template?.thumbnail ? formatImageURL(template.thumbnail) : null

  return (
    <div className="relative aspect-3/2 w-full overflow-hidden text-white bg-foreground">
      {thumbnail && <img src={thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div aria-hidden className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase opacity-80">Бэлгийн карт</div>
            <div className="mt-1 text-sm truncate">{vendorData?.name || 'Shop.mn'}</div>
          </div>
          {template?.name && <div className="shrink-0 text-[10px] tracking-[0.2em] uppercase opacity-75">{template.name}</div>}
        </div>
        <div className="min-w-0">
          {message && <div className="mb-3 text-xs italic opacity-90 line-clamp-2 leading-relaxed">“{message}”</div>}
          <div className="flex items-end justify-between gap-4">
            <div className="text-xs opacity-80 truncate">{recipient || ''}</div>
            <div className="text-3xl sm:text-4xl font-light tabular-nums leading-none">{value ? formatPrice(value) : '—'}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function GiftCard({ templates, vendorData }: { templates: any, vendorData: any }) {
  const pathname = usePathname()
  const f = useGiftCardForm({ templates, vendorData, totalSteps: STEPS.length })
  const messageId = useId()
  const copyId = useId()
  const amountValue = f.amount ? parseInt(f.amount, 10) : 0

  const vendorTemplates = (templates || []).filter((t: any) => t.vendorId)
  const globalTemplates = (templates || []).filter((t: any) => !t.vendorId)

  const renderTemplates = (list: any[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-1 gap-y-5">
      {list.map((template: any) => {
        const selected = f.giftCardTemplateId === template.id
        return (
          <button key={template.id} type="button" onClick={() => f.setGiftCardTemplateId(template.id)} aria-pressed={selected} className={`group text-left ${panelFocusRing}`}>
            <div className={`aspect-3/2 w-full overflow-hidden bg-muted outline-offset-2 transition-[outline-color] duration-150 ${selected ? 'outline-1 outline-foreground' : 'outline-1 outline-transparent'}`}>
              <img src={formatImageURL(template.thumbnail)} alt="" className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-85" />
            </div>
            <div className={`mt-2 text-[13px] ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>{template.name}</div>
          </button>
        )
      })}
    </div>
  )

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${panelFocusRing}`}>Нүүр</a>
        <span aria-hidden>/</span>
      </nav>
      <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight text-foreground">Бэлгийн карт</h1>
      <p className="mt-3 text-sm text-muted-foreground">Дизайн, дүнгээ сонгоод хайртай хүндээ бэлэглээрэй</p>

      <div className="mt-10 lg:mt-14 flex flex-col-reverse lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-12 lg:gap-20">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <GiftCardPreview template={f.selectedTemplate} amount={f.amount} recipient={f.fullName} message={f.message} vendorData={vendorData} />
          <dl className="mt-8 divide-y divide-border border-y border-border text-[13px]">
            <div className="flex justify-between gap-4 py-3"><dt className="text-muted-foreground">Дизайн</dt><dd className="truncate">{f.selectedTemplate?.name || '—'}</dd></div>
            <div className="flex justify-between gap-4 py-3"><dt className="text-muted-foreground">Хүлээн авагч</dt><dd className="truncate">{f.fullName || '—'}</dd></div>
            <div className="flex justify-between gap-4 py-4 text-sm"><dt>Нийт</dt><dd className="tabular-nums">{formatPrice(amountValue || 0)}</dd></div>
          </dl>
        </aside>

        <div className="min-w-0">
          <ol className="flex items-center gap-6 text-[13px]" aria-label="Алхамууд">
            {STEPS.map((label, i) => (
              <li key={label} aria-current={i === f.step ? 'step' : undefined} className={`flex items-baseline gap-2 ${i === f.step ? 'text-foreground' : i < f.step ? 'text-foreground/70' : 'text-muted-foreground'}`}>
                <span className="tabular-nums text-xs">{String(i + 1).padStart(2, '0')}</span>
                <span className="hidden sm:inline">{label}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4 h-px bg-border">
            <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${((f.step + 1) / STEPS.length) * 100}%` }} />
          </div>

          <div className="mt-10 min-h-96">
            {f.step === 0 && (
              <section>
                <h2 className="text-xl sm:text-2xl tracking-tight">Картын дизайныг сонгоно уу</h2>
                <p className="mt-2 text-[13px] text-muted-foreground">Сонгосон дизайн урьдчилсан харагдац дээр шинэчлэгдэнэ.</p>
                <div className="mt-8 space-y-10">
                  {vendorTemplates.length > 0 && (
                    <div>
                      <div className={`mb-4 ${eyebrow}`}>Дэлгүүрийн дизайн</div>
                      {renderTemplates(vendorTemplates)}
                    </div>
                  )}
                  {globalTemplates.length > 0 && (
                    <div>
                      {vendorTemplates.length > 0 && <div className={`mb-4 ${eyebrow}`}>Бусад дизайн</div>}
                      {renderTemplates(globalTemplates)}
                    </div>
                  )}
                </div>
              </section>
            )}

            {f.step === 1 && (
              <section>
                <h2 className="text-xl sm:text-2xl tracking-tight">Үнийн дүнг сонгоно уу</h2>
                <p className="mt-2 text-[13px] text-muted-foreground tabular-nums">{formatPrice(GIFT_CARD_MIN_AMOUNT)} – {formatPrice(GIFT_CARD_MAX_AMOUNT)} хооронд</p>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {GIFT_CARD_PRESETS.map(p => {
                    const selected = f.amount === String(p)
                    return (
                      <button key={p} type="button" onClick={() => f.setAmount(String(p))} aria-pressed={selected} className={`h-14 border text-sm tabular-nums transition-colors duration-150 ${selected ? 'border-foreground bg-foreground text-background' : 'border-border hover:border-foreground'} ${panelFocusRing}`}>
                        {formatPrice(p)}
                      </button>
                    )
                  })}
                </div>
                <TextField className="mt-6" label="Өөр дүн" inputMode="numeric" placeholder={`${GIFT_CARD_MIN_AMOUNT}`} value={f.amount} onChange={e => f.setAmount(e.target.value.replace(/[^0-9]/g, ''))} description="Гараар мөнгөн дүнг оруулах" />
              </section>
            )}

            {f.step === 2 && (
              <section className="flex flex-col gap-5">
                <div>
                  <h2 className="text-xl sm:text-2xl tracking-tight">Хүлээн авагчийн мэдээлэл</h2>
                  <p className="mt-2 text-[13px] text-muted-foreground">Картыг сонгосон огноонд автоматаар илгээнэ.</p>
                </div>
                {f.isGuest && (
                  <TextField label="Таны и-мэйл" type="email" placeholder="me@mail.mn" value={f.senderEmail} onChange={e => f.setSenderEmail(e.target.value || f.session?.user?.email || '')} autoComplete="email" required />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <TextField label="Хүлээн авагчийн нэр" placeholder="Хүлээн авагчийн нэр" value={f.fullName} onChange={e => f.setFullName(e.target.value)} required />
                  <TextField label="Хүлээн авагчийн и-мэйл" type="email" placeholder="example@mail.mn" value={f.email} onChange={e => f.setEmail(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor={messageId} className="block mb-2 text-[13px]">Зурвас</label>
                  <textarea id={messageId} rows={4} value={f.message} onChange={e => f.setMessage(e.target.value)} placeholder="Сайн уу…" className="block w-full px-3 py-3 text-sm bg-background text-foreground border border-border rounded-none placeholder:text-muted-foreground transition-colors duration-150 focus:border-foreground focus:ring-0 focus:outline-none resize-none" />
                </div>
                <DateTimePicker date={f.deliveryDate} setDate={(d: Date | undefined) => f.setDeliveryDate(d || null)} label="Илгээх огноо" className="w-full!" color="zinc-800" />
                <label htmlFor={copyId} className="flex items-start gap-3 cursor-pointer">
                  <input id={copyId} type="checkbox" checked={f.sendCopy} onChange={e => f.setSendCopy(e.target.checked)} className="mt-0.5 size-4 rounded-none border-foreground/40 text-foreground focus:ring-foreground focus:ring-offset-0" />
                  <span>
                    <span className="block text-[13px]">Хуулбар авах</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Бэлгийн картыг илгээх үед таны и-мэйл хаягт картын хуулбар илгээгдэнэ.</span>
                  </span>
                </label>
              </section>
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-border flex items-center justify-between gap-4">
            <button type="button" onClick={f.back} disabled={f.step === 0} className={`text-[13px] text-muted-foreground transition-colors duration-150 hover:text-foreground disabled:opacity-0 disabled:pointer-events-none ${panelFocusRing}`}>Буцах</button>
            {f.step < STEPS.length - 1 ? (
              <button type="button" onClick={f.next} className={solidButton}>Үргэлжлүүлэх</button>
            ) : (
              <button type="button" onClick={f.submit} disabled={f.loading} className={solidButton}>{f.loading ? 'Захиалж байна…' : 'Захиалах'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
